/**
 * Song & Timing feature module (W2-08 timing; DESIGN.md §4.15, §5.2; WORKPACKAGES Appendix B: `timing.*`).
 *
 * Provides:
 *  - workspace panels `timing.left`, `timing.main`, `timing.bottom`;
 *  - `DIALOG.latencyCalibration` (modal, writes settings.audio.latency.calibrationMs itself);
 *  - the one-time audio latency banner (status item, slot 'banner');
 *  - the wizard step `beat`;
 *  - commands: `timing.calibrate` and `timing.detectTempo` (contracted, no arguments), `timing.detectOffset`,
 *    `timing.tap` (B), `timing.tapReset`, `timing.tapUse`, `timing.addTempoAtPlayhead` (Shift+B),
 *    `timing.addMeterAtPlayhead`, `timing.offsetEarlier` / `timing.offsetLater` (Alt+, / Alt+. 1 ms, with Shift
 *    10 ms; Alt-free: , / . in the Song & Timing workspace), `timing.multiplyTempo`, `timing.playWithClicks`,
 *    `timing.tooEarly` / `timing.justRight` / `timing.tooLate`, `timing.confirm`, `timing.foldInfoOffset`,
 *    `timing.setPolicy`, `timing.beatZeroHere` (M over the song overview, snapped to the attack like a click);
 *  - feature settings `ext.timing.autoDetect`, `ext.timing.scrubOnHover`, `ext.timing.alignmentRows`;
 *  - help topics (timing, latency, meter);
 *  - `init`: the latency banner on the first playback, ending "Play with clicks" when playback stops, and the
 *    feature setting defaults. (The song's waveform in the chart timeline is installed by the timeline panel,
 *    W2-03, which also honours the waveform toggle: CR-W2-08-3.)
 * The keys bound to single letters act in the Song & Timing workspace only (`workspace == timing`).
 */

import { DIALOG } from '../../app/dialogs/types';
import type {
  AppServices,
  CommandContext,
  CommandDef,
  FeatureModule,
  HelpTopicDef,
  KeyBinding,
  SettingDef,
} from '../../app/registry/types';
import type { TempoEditPolicy } from '../../core/commands/types';
import type { MessageRef } from '../../core/util/types';
import { BeatStep } from './BeatStep';
import { LatencyCalibrationDialog } from './LatencyCalibration';
import { AvLabRoute } from './AvLab';
import { LatencyHintBanner, watchFirstPlayback } from './LatencyHint';
import { resetTaps, tapNow, tapReading } from './tapActions';
import { m } from './timing.messages';
import {
  addMeterAtPlayhead,
  addTempoAtPlayhead,
  answerByEar,
  beatZeroAt,
  detectOffset,
  detectTempo,
  openCalibration,
  stopClicks,
  togglePlayWithClicks,
} from './timingActions';
import { foldPerChart, multiplyTempo, nudgeOffset, setConfirmed, setSongBpm } from './timingEdits';
import { timingAnalysisFor } from './timingAnalysis';
import { perChartAdjustmentMs, snapToOnset } from './timingModel';
import { currentPolicy, setPolicy, timingSessionFor } from './timingSession';
import { TimingBottomPanel, TimingLeftPanel, TimingMainPanel } from './TimingWorkspace';

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

const ref = (key: string): MessageRef => ({ key });

function projectOpen(ctx: Pick<AppServices, 'store'>): true | MessageRef {
  return ctx.store === null ? ref(m.noProjectReason) : true;
}

function songOpen(ctx: Pick<AppServices, 'store'>): true | MessageRef {
  if (ctx.store === null) return ref(m.noProjectReason);
  return ctx.store.doc.meta.music === null ? ref(m.noSongReason) : true;
}

function numberArg(args: unknown, key: string): number | undefined {
  if (typeof args === 'number') return Number.isFinite(args) ? args : undefined;
  if (typeof args !== 'object' || args === null) return undefined;
  const v = (args as Readonly<Record<string, unknown>>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function policyArg(args: unknown): TempoEditPolicy | undefined {
  const v =
    typeof args === 'string'
      ? args
      : typeof args === 'object' && args !== null
        ? (args as { policy?: unknown }).policy
        : undefined;
  return v === 'keepBeats' || v === 'keepTime' ? v : undefined;
}

const timingMenu = (order: number) => [{ menu: 'timing.context' as const, group: 'timing', order }];

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

export const TIMING_COMMANDS: readonly CommandDef[] = [
  {
    id: 'timing.calibrate',
    title: m.cmdCalibrate,
    category: 'Timing',
    icon: 'Timer',
    level: 'guided',
    aliases: ['latency', 'audio delay', 'bluetooth', 'calibration', 'output latency', '延迟', '校准'],
    menus: timingMenu(90),
    async run(ctx) {
      await openCalibration(ctx);
    },
  },
  {
    id: 'timing.detectTempo',
    title: m.cmdDetectTempo,
    category: 'Timing',
    icon: 'AudioWaveform',
    level: 'guided',
    aliases: ['bpm', 'tempo', 'detect bpm', 'beat detection', 'analyse', 'BPM检测', '测速'],
    enabled: songOpen,
    menus: timingMenu(10),
    async run(ctx) {
      if (ctx.editor.getState().workspace !== 'timing') ctx.nav.setWorkspace('timing');
      await detectTempo(ctx, true);
    },
  },
  {
    id: 'timing.detectOffset',
    title: m.cmdDetectOffset,
    category: 'Timing',
    icon: 'Crosshair',
    level: 'guided',
    aliases: ['offset', 'first beat', 'beat 0', 'sync', 'align', '偏移', '延迟'],
    enabled: songOpen,
    menus: timingMenu(20),
    async run(ctx) {
      await detectOffset(ctx);
    },
  },
  {
    id: 'timing.tap',
    title: m.cmdTap,
    category: 'Timing',
    icon: 'Hand',
    level: 'guided',
    aliases: ['tap tempo', 'tap bpm', 'tap along'],
    hint: 30,
    enabled: projectOpen,
    run(ctx) {
      tapNow(ctx);
    },
  },
  {
    id: 'timing.tapReset',
    title: m.cmdTapReset,
    category: 'Timing',
    icon: 'RotateCcw',
    level: 'guided',
    run(ctx) {
      resetTaps(ctx);
    },
  },
  {
    id: 'timing.tapUse',
    title: m.cmdTapUse,
    category: 'Timing',
    icon: 'Check',
    level: 'guided',
    enabled: (ctx) => {
      const open = projectOpen(ctx);
      if (open !== true) return open;
      return tapReading(ctx).bpm === null ? ref(m.noTapReason) : true;
    },
    run(ctx) {
      const bpm = tapReading(ctx).bpm;
      if (bpm === null) return;
      if (setSongBpm(ctx, Math.round(bpm * 100) / 100)) resetTaps(ctx);
    },
  },
  {
    id: 'timing.addTempoAtPlayhead',
    title: m.cmdAddTempo,
    category: 'Timing',
    icon: 'Plus',
    level: 'guided',
    aliases: ['bpm change', 'tempo change', 'add bpm', 'variable bpm', '变速'],
    hint: 20,
    enabled: projectOpen,
    menus: timingMenu(30),
    run(ctx) {
      addTempoAtPlayhead(ctx);
    },
  },
  {
    id: 'timing.addMeterAtPlayhead',
    title: m.cmdAddMeter,
    category: 'Timing',
    icon: 'ListPlus',
    level: 'guided',
    aliases: ['meter', 'time signature', 'beats per bar', '3/4', '7/8', '拍号'],
    enabled: projectOpen,
    menus: timingMenu(40),
    run(ctx) {
      addMeterAtPlayhead(ctx);
    },
  },
  {
    id: 'timing.offsetEarlier',
    title: m.cmdOffsetEarlier,
    category: 'Timing',
    icon: 'ChevronLeft',
    level: 'guided',
    aliases: ['offset', 'nudge', 'beat 0 earlier'],
    repeat: true,
    hint: 10,
    enabled: projectOpen,
    run(ctx, args) {
      nudgeOffset(ctx, -(numberArg(args, 'ms') ?? 1));
    },
  },
  {
    id: 'timing.offsetLater',
    title: m.cmdOffsetLater,
    category: 'Timing',
    icon: 'ChevronRight',
    level: 'guided',
    aliases: ['offset', 'nudge', 'beat 0 later'],
    repeat: true,
    hint: 10,
    enabled: projectOpen,
    run(ctx, args) {
      nudgeOffset(ctx, numberArg(args, 'ms') ?? 1);
    },
  },
  {
    id: 'timing.multiplyTempo',
    title: m.cmdMultiply,
    category: 'Timing',
    icon: 'Divide',
    level: 'guided',
    aliases: ['double tempo', 'half tempo', 'x2', '/2', 'x1.5', 'octave'],
    enabled: projectOpen,
    args: {
      kind: 'choice',
      label: ref(m.cmdMultiplyArg),
      options: [
        { value: 2, label: ref(m.times2Label) },
        { value: 0.5, label: ref(m.div2Label) },
        { value: 1.5, label: ref(m.times15Label) },
        { value: 2 / 3, label: ref(m.div15Label) },
      ],
    },
    run(ctx, args) {
      const factor = numberArg(args, 'factor');
      if (factor !== undefined && factor > 0) multiplyTempo(ctx, factor);
    },
  },
  {
    id: 'timing.playWithClicks',
    title: m.cmdPlayClicks,
    category: 'Timing',
    icon: 'Drum',
    level: 'guided',
    aliases: ['metronome', 'listen', 'check timing', 'clicks'],
    enabled: songOpen,
    checked: (ctx) => timingSessionFor(ctx).get().clicks && ctx.audio.transport.state.playing,
    menus: timingMenu(50),
    async run(ctx) {
      await togglePlayWithClicks(ctx);
    },
  },
  {
    id: 'timing.tooEarly',
    title: m.cmdTooEarly,
    category: 'Timing',
    icon: 'ChevronLeft',
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      answerByEar(ctx, 'tooEarly');
    },
  },
  {
    id: 'timing.justRight',
    title: m.cmdJustRight,
    category: 'Timing',
    icon: 'Check',
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      answerByEar(ctx, 'justRight');
    },
  },
  {
    id: 'timing.tooLate',
    title: m.cmdTooLate,
    category: 'Timing',
    icon: 'ChevronRight',
    level: 'guided',
    enabled: projectOpen,
    run(ctx) {
      answerByEar(ctx, 'tooLate');
    },
  },
  {
    id: 'timing.confirm',
    title: m.cmdConfirm,
    category: 'Timing',
    icon: 'CircleCheck',
    level: 'guided',
    aliases: ['timing confirmed', 'confirm bpm', 'confirm offset'],
    enabled: projectOpen,
    checked: (ctx) => ctx.store?.doc.timing.confirmed === true,
    menus: timingMenu(60),
    run(ctx) {
      const store = ctx.store;
      if (store !== null) setConfirmed(ctx, !store.doc.timing.confirmed);
    },
  },
  {
    id: 'timing.foldInfoOffset',
    title: m.cmdFold,
    category: 'Timing',
    icon: 'Merge',
    level: 'full',
    aliases: ['info.yml offset', 'per-chart offset', 'fold offset'],
    enabled: (ctx) => {
      const store = ctx.store;
      if (store === null) return ref(m.noProjectReason);
      return perChartAdjustmentMs(store.doc.pkg) === 0 ? ref(m.nothingToFold) : true;
    },
    run(ctx) {
      foldPerChart(ctx);
    },
  },
  {
    id: 'timing.setPolicy',
    title: m.cmdPolicy,
    category: 'Timing',
    icon: 'ArrowLeftRight',
    level: 'guided',
    aliases: ['keep beats', 'keep time', 'retime', 'anchor'],
    enabled: projectOpen,
    args: {
      kind: 'choice',
      label: ref(m.policyLabel),
      options: [
        { value: 'keepBeats', label: ref(m.policyKeepBeats) },
        { value: 'keepTime', label: ref(m.policyKeepTime) },
      ],
    },
    run(ctx, args) {
      const p = policyArg(args) ?? (currentPolicy(ctx) === 'keepBeats' ? 'keepTime' : 'keepBeats');
      setPolicy(ctx, p);
    },
  },
  {
    id: 'timing.beatZeroHere',
    title: m.cmdBeatZeroHere,
    category: 'Timing',
    icon: 'MousePointerClick',
    level: 'guided',
    enabled: projectOpen,
    run(ctx: CommandContext) {
      const sec = timingSessionFor(ctx).get().overviewHoverSec;
      if (sec === null) return;
      // Like a click on the overview: snapped to the strongest attack within 40 ms.
      const audio = ctx.store?.doc.meta.music ?? null;
      const onsets = audio === null ? null : timingAnalysisFor(ctx).get(audio).onsets;
      beatZeroAt(ctx, snapToOnset(onsets, sec));
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Keys (both presets: DESIGN.md §8.2 "Song & Timing | Tap tempo · add tempo point at playhead | B · Shift+B",
// "Offset -1 / +1 ms (Shift +-10 ms) | Alt+, / Alt+.")
// ---------------------------------------------------------------------------------------------------------------

const IN_TIMING = 'workspace == timing';
/**
 * The 10 ms nudges: in Alt-free mode they become Shift+Comma / Shift+Period, which the shell binds to the motion and
 * curve columns' key navigation. Those columns never have focus in the timing workspace; saying so keeps the two
 * contexts disjoint for the keymap's conflict check.
 */
const IN_TIMING_SHIFT_NUDGE = `${IN_TIMING} && !focus.motion && !focus.curve`;

const TIMING_KEYS: readonly KeyBinding[] = [
  { chord: 'KeyB', command: 'timing.tap', when: IN_TIMING },
  { chord: 'Shift+KeyB', command: 'timing.addTempoAtPlayhead', when: IN_TIMING },
  { chord: 'Alt+Comma', command: 'timing.offsetEarlier', args: { ms: 1 }, when: IN_TIMING, altFree: 'Comma' },
  { chord: 'Alt+Period', command: 'timing.offsetLater', args: { ms: 1 }, when: IN_TIMING, altFree: 'Period' },
  {
    chord: 'Alt+Shift+Comma',
    command: 'timing.offsetEarlier',
    args: { ms: 10 },
    when: IN_TIMING_SHIFT_NUDGE,
    altFree: 'Shift+Comma',
  },
  {
    chord: 'Alt+Shift+Period',
    command: 'timing.offsetLater',
    args: { ms: 10 },
    when: IN_TIMING_SHIFT_NUDGE,
    altFree: 'Shift+Period',
  },
  { chord: 'KeyM', command: 'timing.beatZeroHere', when: `${IN_TIMING} && timing.overviewHover` },
];

// ---------------------------------------------------------------------------------------------------------------
// Settings and help
// ---------------------------------------------------------------------------------------------------------------

const TIMING_SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.timing.autoDetect',
    title: m.setAutoDetect,
    description: m.setAutoDetectDesc,
    level: 'guided',
    kind: 'boolean',
  },
  {
    path: 'ext.timing.scrubOnHover',
    title: m.setScrub,
    description: m.setScrubDesc,
    level: 'full',
    kind: 'boolean',
  },
  {
    path: 'ext.timing.alignmentRows',
    title: m.setRows,
    description: m.setRowsDesc,
    level: 'full',
    kind: 'number',
    min: 0,
    max: 64,
    step: 4,
  },
];

/** Defaults written once when a feature setting has never been set (so the Settings page shows the real value). */
export const TIMING_SETTING_DEFAULTS: Readonly<Record<string, boolean | number>> = {
  'timing.autoDetect': true,
  'timing.scrubOnHover': true,
  'timing.alignmentRows': 0,
};

const HELP: readonly HelpTopicDef[] = [
  {
    id: 'timing.setTiming',
    title: m.helpTimingTitle,
    body: m.helpTimingBody,
    showMe: { panel: 'timing.left' },
    level: 'guided',
  },
  {
    id: 'timing.latency',
    title: m.helpLatencyTitle,
    body: m.helpLatencyBody,
    showMe: { panel: 'timing.bottom' },
    level: 'guided',
  },
  {
    id: 'timing.meter',
    title: m.helpMeterTitle,
    body: m.helpMeterBody,
    showMe: { panel: 'timing.left' },
    level: 'guided',
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------------------------------------------

/** The module's startup work (exported for tests). */
export function initTiming(s: AppServices): () => void {
  const offs: Array<() => void> = [];
  offs.push(watchFirstPlayback(s));
  offs.push(
    s.audio.transport.onChange((st) => {
      if (!st.playing && timingSessionFor(s).get().clicks) stopClicks(s);
    }),
  );
  // UX-023 (CR-W3-01-5): the analysis peaks and onset envelopes must not outlive the open project.
  let hadStore = s.store !== null;
  offs.push(
    s.onChange(() => {
      const open = s.store !== null;
      if (hadStore && !open) timingAnalysisFor(s).release();
      hadStore = open;
    }),
  );
  void s.settings.ready.then(() => {
    for (const [key, value] of Object.entries(TIMING_SETTING_DEFAULTS)) {
      if (s.settings.getExt(key) === undefined) s.settings.setExt(key, value);
    }
  });
  return () => {
    for (const off of offs) off();
  };
}

export default {
  id: 'timing',
  commands: TIMING_COMMANDS,
  keybindings: { default: TIMING_KEYS, rpe: TIMING_KEYS },
  panels: [
    {
      id: 'timing.left',
      title: m.panelLeft,
      icon: 'AudioLines',
      slot: 'timing.left',
      order: 10,
      level: 'guided',
      component: TimingLeftPanel,
    },
    {
      id: 'timing.main',
      title: m.panelMain,
      icon: 'AudioWaveform',
      slot: 'timing.main',
      order: 10,
      level: 'guided',
      component: TimingMainPanel,
    },
    {
      id: 'timing.bottom',
      title: m.panelBottom,
      icon: 'Ear',
      slot: 'timing.bottom',
      order: 10,
      level: 'guided',
      component: TimingBottomPanel,
    },
  ],
  dialogs: [{ id: DIALOG.latencyCalibration, kind: 'modal', component: LatencyCalibrationDialog }],
  statusItems: [{ id: 'timing.latencyHint', slot: 'banner', order: 50, component: LatencyHintBanner }],
  // A/V sync lab for the human check H4 (Appendix C): not linked from the UI.
  routes: [{ path: '#/dev/av', component: AvLabRoute }],
  wizardSteps: [{ id: 'beat', order: 40, title: m.stepTitle, skippable: true, component: BeatStep }],
  settings: TIMING_SETTINGS,
  helpTopics: HELP,
  whenAtoms: (s) => ({
    'timing.overviewHover': () => timingSessionFor(s).get().overviewHoverSec !== null,
  }),
  init: initTiming,
} satisfies FeatureModule;
